// Review gate for MACHINE OUTPUT — one module owns every rule, so the canvas queue, the Estimate flag and
// the save paths can never disagree about what is "machine output" or what is "unreviewed".
//
// Machine output = rows the software produced rather than a person:
//   rooms     AI-extracted         TakeoffRooms.DetectionStatus === 'proposed'   (accepted | rejected = deleted)
//   symbols   engine-generated     TakeoffConditionsDrawn with AutoSystem        (HEPA / light ceiling symbols)
//   walls     rule-applied         TakeoffConditionsDrawn with AutoWallKey
//   elements  template-seeded      RoomElements with SourceTemplateEquipmentID
// A symbol / wall / element carries ReviewStatus: 'unreviewed' | 'accepted' | 'corrected' (NULL = hand-made,
// never machine output). A person ACCEPTS it (sp_AcceptMachineItems) or EDITS it, which stamps 'corrected'
// (decision: editing counts as reviewing — the estimator never has to edit and then also accept).
// Unreviewed output still PRICES (decision); the gate is visibility and a record of who reviewed what.
//
// Pure: no I/O.

export const REVIEW_STATUSES = ["unreviewed", "accepted", "corrected"];

export const isMachineCondition = (c) => !!(c && (c.AutoSystem || c.AutoWallKey));
export const machineKindOfCondition = (c) => (c?.AutoSystem ? "symbol" : c?.AutoWallKey ? "wall" : null);
export const isMachineElement = (e) => !!(e && e.SourceTemplateEquipmentID != null);

// A machine row with no status yet (a legacy client, or a row inserted before the backfill) is unreviewed:
// nobody has looked at it through this gate.
export const isUnreviewedRoom = (r) => r?.DetectionStatus === "proposed";
export const isUnreviewedCondition = (c) => isMachineCondition(c) && (c.ReviewStatus == null || c.ReviewStatus === "unreviewed");
export const isUnreviewedElement = (e) => isMachineElement(e) && (e.ReviewStatus == null || e.ReviewStatus === "unreviewed");

/**
 * The fields to send with a PERSON's edit of a machine row, so the edit stamps it 'corrected'.
 * Empty for a hand-made row and for one that has already been reviewed.
 */
export function correctionStamp(row, kind, reviewer) {
  const unreviewed = kind === "element" ? isUnreviewedElement(row) : isUnreviewedCondition(row);
  return unreviewed ? { review_status: "corrected", reviewed_by: reviewer || null } : {};
}

/** The same for a room: editing an AI proposal accepts it (the proposal itself stays frozen for comparison). */
export function roomCorrectionPatch(room, reviewer) {
  return isUnreviewedRoom(room) ? { DetectionStatus: "accepted", DetectionReviewedBy: reviewer || null } : {};
}

const SYSTEM_LABEL = { hepa: "HEPA", light: "light" };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const roomName = (roomsById, id) => roomsById.get(id)?.RoomName || (id != null ? `Room ${id}` : "No room");

const entityKey = (c) => (c.Kind === "item" ? `item:${c.ItemID}` : c.Kind === "assembly" ? `assembly:${c.AssemblyID}` : null);

/**
 * Everything awaiting review, grouped for the queue, plus totals.
 *   rooms, conditions, elements — the rows (the canvas passes the active drawing's; the Estimate passes all)
 *   quantityOfCondition(c) -> number   quantityOfElement(e) -> number   unitPriceOf('item:7') -> sell $/unit
 *     (all optional — without them no value is computed)
 * Returns { groups, counts: { rooms, symbols, walls, elements, total }, valueAtStake }.
 * A group: { key, kind, label, roomId, sheetId, count, accept: { rooms, conditions, elements }, value }.
 */
export function summarizeUnreviewed({ rooms = [], conditions = [], elements = [], quantityOfCondition, quantityOfElement, unitPriceOf } = {}) {
  const roomsById = new Map(rooms.map(r => [r.RoomID, r]));
  const priced = typeof unitPriceOf === "function";
  const groups = new Map();
  const group = (key, init) => groups.get(key) || groups.set(key, { key, count: 0, value: 0, accept: { rooms: [], conditions: [], elements: [] }, ...init }).get(key);

  for (const r of rooms) {
    if (!isUnreviewedRoom(r)) continue;
    const g = group(`room:${r.RoomID}`, { kind: "room", roomId: r.RoomID, sheetId: r.SheetID ?? null, label: `${r.RoomName || `Room ${r.RoomID}`} — AI proposal` });
    g.count += 1; g.accept.rooms.push(r.RoomID);
  }

  for (const c of conditions) {
    if (!isUnreviewedCondition(c)) continue;
    const kind = machineKindOfCondition(c);
    const key = kind === "symbol" ? `symbol:${c.RoomID ?? "x"}:${c.AutoSystem}` : `wall:${c.SheetID ?? "x"}:${c.RoomID ?? "x"}`;
    const label = kind === "symbol"
      ? (n) => `${roomName(roomsById, c.RoomID)} — ${plural(n, `generated ${SYSTEM_LABEL[c.AutoSystem] || c.AutoSystem} symbol`, `generated ${SYSTEM_LABEL[c.AutoSystem] || c.AutoSystem} symbols`)}`
      : (n) => `${roomName(roomsById, c.RoomID)} — ${plural(n, "auto-applied wall segment", "auto-applied wall segments")}`;
    const g = group(key, { kind, roomId: c.RoomID ?? null, sheetId: c.SheetID ?? null, labelFor: label, conditionIds: [] });
    g.count += 1; g.accept.conditions.push(c.TakeoffConditionDrawnID); g.conditionIds.push(c.TakeoffConditionDrawnID);
    if (priced && typeof quantityOfCondition === "function") {
      const ek = entityKey(c);
      const p = ek ? unitPriceOf(ek) : 0;
      const q = quantityOfCondition(c);
      if (p > 0 && Number.isFinite(q)) g.value += p * q;
    }
  }

  for (const e of elements) {
    if (!isUnreviewedElement(e)) continue;
    const g = group(`element:${e.RoomID}`, { kind: "element", roomId: e.RoomID, sheetId: roomsById.get(e.RoomID)?.SheetID ?? null,
      labelFor: (n) => `${roomName(roomsById, e.RoomID)} — ${plural(n, "element from its room template", "elements from its room template")}` });
    g.count += 1; g.accept.elements.push(e.RoomElementID);
    if (priced && typeof quantityOfElement === "function") {
      const ek = e.Kind === "item" ? `item:${e.ItemID}` : `assembly:${e.AssemblyID}`;
      const p = unitPriceOf(ek);
      const q = quantityOfElement(e);
      if (p > 0 && Number.isFinite(q)) g.value += p * q;
    }
  }

  const list = [...groups.values()].map(g => ({ ...g, label: g.label || g.labelFor(g.count) }));
  const order = { room: 0, symbol: 1, element: 2, wall: 3 };
  list.sort((a, b) => order[a.kind] - order[b.kind] || String(a.label).localeCompare(b.label, undefined, { numeric: true }));
  const counts = { rooms: 0, symbols: 0, walls: 0, elements: 0, total: 0 };
  let valueAtStake = 0;
  for (const g of list) { counts[g.kind === "room" ? "rooms" : g.kind === "symbol" ? "symbols" : g.kind === "wall" ? "walls" : "elements"] += g.count; counts.total += g.count; valueAtStake += g.value; }
  return { groups: list, counts, valueAtStake: priced ? valueAtStake : null };
}

/** The ids to send to sp_AcceptMachineItems for one or more groups (de-duplicated). */
export function acceptPayload(groups) {
  const out = { rooms: new Set(), conditions: new Set(), elements: new Set() };
  for (const g of Array.isArray(groups) ? groups : [groups]) {
    g?.accept?.rooms.forEach(i => out.rooms.add(i));
    g?.accept?.conditions.forEach(i => out.conditions.add(i));
    g?.accept?.elements.forEach(i => out.elements.add(i));
  }
  return { rooms: [...out.rooms], conditions: [...out.conditions], elements: [...out.elements] };
}

/** One line for the Estimate: "12 unreviewed machine items (2 rooms, 6 symbols, 3 walls, 1 element) …". */
export function describeUnreviewed(counts) {
  if (!counts || !counts.total) return "";
  const parts = [];
  if (counts.rooms) parts.push(plural(counts.rooms, "AI room", "AI rooms"));
  if (counts.symbols) parts.push(plural(counts.symbols, "generated symbol", "generated symbols"));
  if (counts.walls) parts.push(plural(counts.walls, "auto wall", "auto walls"));
  if (counts.elements) parts.push(plural(counts.elements, "template element", "template elements"));
  return `${plural(counts.total, "unreviewed machine item", "unreviewed machine items")} (${parts.join(", ")})`;
}
